import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { getLessonById, getBlockById } from '../data/curriculum';
import { X, Volume2, VolumeX } from 'lucide-react';

// Exercise components
import SeeAndChoose from '../components/exercises/SeeAndChoose';
import MatchGroupToNumber from '../components/exercises/MatchGroupToNumber';
import MatchNumberToGroup from '../components/exercises/MatchNumberToGroup';
import CompareGroups from '../components/exercises/CompareGroups';
import AnimatedAddRemove from '../components/exercises/AnimatedAddRemove';
import HiddenBox from '../components/exercises/HiddenBox';
import MentalMath from '../components/exercises/MentalMath';

const LessonPage = ({ onComplete }) => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const lesson = getLessonById(parseInt(lessonId));
  const block = lesson ? getBlockById(lesson.blockId) : null;

  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const totalExercises = lesson?.exercises?.length || 0;
  const progressPercent = totalExercises > 0 ? ((currentExerciseIndex) / totalExercises) * 100 : 0;
  const currentExercise = lesson?.exercises?.[currentExerciseIndex];

  const handleAnswer = useCallback((isCorrect) => {
    setAnswers(prev => [...prev, isCorrect]);
    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
    }

    // Move to next exercise or complete lesson
    if (currentExerciseIndex < totalExercises - 1) {
      setTimeout(() => {
        setCurrentExerciseIndex(prev => prev + 1);
      }, 100);
    } else {
      // Lesson complete
      const finalCorrect = isCorrect ? correctAnswers + 1 : correctAnswers;
      const accuracy = Math.round((finalCorrect / totalExercises) * 100);
      
      setTimeout(() => {
        navigate(`/lesson/${lessonId}/complete`, {
          state: {
            accuracy,
            correctAnswers: finalCorrect,
            totalExercises,
            lessonId: parseInt(lessonId)
          }
        });
        onComplete(parseInt(lessonId), accuracy);
      }, 500);
    }
  }, [currentExerciseIndex, totalExercises, correctAnswers, navigate, lessonId, onComplete]);

  const handleExit = () => {
    if (currentExerciseIndex > 0) {
      // Confirm exit if lesson is in progress
      if (window.confirm('Вы уверены, что хотите выйти? Прогресс не сохранится.')) {
        navigate('/home');
      }
    } else {
      navigate('/home');
    }
  };

  const renderExercise = () => {
    if (!currentExercise) return null;

    const exerciseKey = `${lessonId}-${currentExerciseIndex}`;

    switch (currentExercise.type) {
      case 'see-and-choose':
        return <SeeAndChoose key={exerciseKey} exercise={currentExercise} onAnswer={handleAnswer} />;
      case 'match-group-to-number':
        return <MatchGroupToNumber key={exerciseKey} exercise={currentExercise} onAnswer={handleAnswer} />;
      case 'match-number-to-group':
        return <MatchNumberToGroup key={exerciseKey} exercise={currentExercise} onAnswer={handleAnswer} />;
      case 'compare-groups':
        return <CompareGroups key={exerciseKey} exercise={currentExercise} onAnswer={handleAnswer} />;
      case 'animated-add':
      case 'animated-remove':
        return <AnimatedAddRemove key={exerciseKey} exercise={currentExercise} onAnswer={handleAnswer} />;
      case 'hidden-box':
        return <HiddenBox key={exerciseKey} exercise={currentExercise} onAnswer={handleAnswer} />;
      case 'mental-math':
        return <MentalMath key={exerciseKey} exercise={currentExercise} onAnswer={handleAnswer} />;
      default:
        return <div>Неизвестное упражнение</div>;
    }
  };

  if (!lesson) {
    return (
      <div className="app-container min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Урок не найден</p>
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen bg-background flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-4 py-3 flex items-center justify-between border-b border-border"
      >
        {/* Exit button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleExit}
          className="w-10 h-10 rounded-full"
        >
          <X className="w-5 h-5" />
        </Button>

        {/* Progress */}
        <div className="flex-1 mx-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">
              {block?.title} • Урок {lesson.id}
            </span>
            <span className="text-xs font-medium">
              {currentExerciseIndex + 1}/{totalExercises}
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Sound toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSoundEnabled(!soundEnabled)}
          className="w-10 h-10 rounded-full"
        >
          {soundEnabled ? (
            <Volume2 className="w-5 h-5 text-muted-foreground" />
          ) : (
            <VolumeX className="w-5 h-5 text-muted-foreground" />
          )}
        </Button>
      </motion.div>

      {/* Exercise content */}
      <div className="flex-1 flex flex-col">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentExerciseIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="flex-1"
          >
            {renderExercise()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Answer indicators */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="px-4 py-3 flex justify-center gap-2"
      >
        {Array.from({ length: totalExercises }, (_, i) => (
          <motion.div
            key={i}
            initial={i === currentExerciseIndex ? { scale: 0 } : false}
            animate={{ scale: 1 }}
            className={`w-3 h-3 rounded-full transition-colors ${
              i < answers.length
                ? answers[i]
                  ? 'bg-correct'
                  : 'bg-incorrect'
                : i === currentExerciseIndex
                ? 'bg-primary'
                : 'bg-muted'
            }`}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default LessonPage;
